import type { RolodexAdvancedFilterType, RolodexFilterType } from 'types'
import { RolodexAdvancedFilterOperator, RolodexFilterOp } from 'types'

/** URL/UI sentinel for pages with a null or empty `issuer_company_name`. */
export const ISSUER_NO_VENDOR_SENTINEL = '__none__'

type CorpusFilterClause = RolodexFilterType | RolodexAdvancedFilterType

function isAdvancedClause(
  clause: CorpusFilterClause
): clause is RolodexAdvancedFilterType {
  return 'operator' in clause
}

function pageClassificationClause(
  pageClassifications: readonly string[] | undefined
): RolodexFilterType | undefined {
  if (pageClassifications === undefined || pageClassifications.length === 0) {
    return undefined
  }
  return {
    field: 'data',
    key: 'page_classification',
    op: RolodexFilterOp.IN,
    value: [...pageClassifications],
  }
}

function issuerClause(
  issuers: readonly string[] | undefined
): CorpusFilterClause | undefined {
  if (issuers === undefined || issuers.length === 0) return undefined
  const named = issuers.filter((value) => value !== ISSUER_NO_VENDOR_SENTINEL)
  const includesNoVendor = issuers.includes(ISSUER_NO_VENDOR_SENTINEL)
  const namedClause: RolodexFilterType | undefined =
    named.length > 0
      ? {
          field: 'data',
          key: 'issuer_company_name',
          op: RolodexFilterOp.IN,
          value: named,
        }
      : undefined
  const noVendorClause: RolodexFilterType | undefined = includesNoVendor
    ? {
        field: 'data',
        key: 'issuer_company_name',
        op: RolodexFilterOp.FALSY,
      }
    : undefined
  if (namedClause && noVendorClause) {
    return {
      operator: RolodexAdvancedFilterOperator.OR,
      filters: [namedClause, noVendorClause],
    }
  }
  return namedClause ?? noVendorClause
}

export type EvalCorpusFilterOptions = {
  filters?: RolodexFilterType[]
  advanced_filter?: RolodexAdvancedFilterType
}

/**
 * Combines the Type and Vendor corpus filters with an implicit AND. Vendor
 * selections that include the "No vendor" sentinel need an OR group, so any
 * combination lands in `advanced_filter`; a single plain clause still uses
 * the simpler `filters` array most Rolodex loaders expect.
 */
export function evalCorpusFilterOptions(args: {
  pageClassifications: readonly string[] | undefined
  issuers: readonly string[] | undefined
}): EvalCorpusFilterOptions {
  const clauses: CorpusFilterClause[] = []
  const typeClause = pageClassificationClause(args.pageClassifications)
  if (typeClause) clauses.push(typeClause)
  const vendorClause = issuerClause(args.issuers)
  if (vendorClause) clauses.push(vendorClause)
  if (clauses.length === 0) return {}
  if (clauses.length === 1) {
    const [clause] = clauses
    return isAdvancedClause(clause)
      ? { advanced_filter: clause }
      : { filters: [clause] }
  }
  return {
    advanced_filter: {
      operator: RolodexAdvancedFilterOperator.AND,
      filters: clauses,
    },
  }
}
